import { useState } from 'react'
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceLine,
} from 'recharts'
import { useGameStore } from '../../store/gameStore'
import Icon from './Icon'

// 自定义像素风格 Tooltip
interface CustomTooltipProps {
  active?: boolean
  payload?: Array<{
    name?: string
    value?: number
    color?: string
    dataKey?: string
  }>
  label?: string
}

function PixelTooltip({ active, payload, label }: CustomTooltipProps) {
  if (!active || !payload || payload.length === 0) return null

  return (
    <div className="pixel-box p-2.5 bg-[color:var(--color-bg-elevated)] border-2 border-[color:var(--color-gold)] text-xs shadow-lg font-pixel-body">
      <div className="font-pixel text-[10px] text-[color:var(--color-gold)] mb-1">
        ► {label}
      </div>
      <div className="space-y-1">
        {payload.map((item, idx) => (
          <div key={idx} className="flex items-center justify-between gap-3">
            <span className="flex items-center gap-1.5 text-[color:var(--color-text-dim)]">
              <span
                className="w-2 h-2 inline-block border border-black"
                style={{ backgroundColor: item.color }}
              />
              {item.name}:
            </span>
            <span
              className="font-bold"
              style={{ color: item.color }}
            >
              {typeof item.value === 'number'
                ? item.dataKey === 'winRate'
                  ? `${item.value}%`
                  : `${item.value > 0 ? '+' : ''}${item.value}%`
                : item.value}
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}

export default function TradingPerformanceDashboard() {
  const performanceData = useGameStore((s) => s.performanceData)
  const [metricView, setMetricView] = useState<'all' | 'pnl' | 'winRate'>('all')

  if (!performanceData || performanceData.length === 0) return null

  const latest = performanceData[performanceData.length - 1]
  const alpha = latest ? (latest.pnl - latest.benchmark).toFixed(1) : '0'

  return (
    <div className="pixel-box p-4 mb-4">
      {/* 头部标题与视图切换 */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-3 pb-2 border-b border-[color:var(--color-border)]">
        <div className="font-pixel text-[10px] text-[color:var(--color-gold)] tracking-wider inline-flex items-center gap-1.5">
          <Icon name="chart" size={13} />
          ► 交易战绩趋势 (Trading Performance)
        </div>
        <div className="flex items-center gap-1 self-start sm:self-auto">
          <button
            onClick={() => setMetricView('all')}
            className={
              'pixel-button text-[9px] px-2 py-0.5 ' +
              (metricView === 'all' ? 'pixel-button-primary' : '')
            }
          >
            综合
          </button>
          <button
            onClick={() => setMetricView('pnl')}
            className={
              'pixel-button text-[9px] px-2 py-0.5 ' +
              (metricView === 'pnl' ? 'pixel-button-primary' : '')
            }
          >
            收益率
          </button>
          <button
            onClick={() => setMetricView('winRate')}
            className={
              'pixel-button text-[9px] px-2 py-0.5 ' +
              (metricView === 'winRate' ? 'pixel-button-primary' : '')
            }
          >
            胜率
          </button>
        </div>
      </div>

      {/* 战绩关键指标统计 */}
      <div className="grid grid-cols-3 gap-2 mb-4">
        <div className="pixel-box p-2 text-center bg-[color:var(--color-bg-elevated)]">
          <div className="font-pixel text-[8px] text-[color:var(--color-text-dim)] mb-0.5">
            模拟累计收益
          </div>
          <div className="font-pixel text-lg text-[color:var(--color-bull)]">
            {latest && latest.pnl >= 0 ? `+${latest.pnl}%` : `${latest?.pnl}%`}
          </div>
        </div>
        <div className="pixel-box p-2 text-center bg-[color:var(--color-bg-elevated)]">
          <div className="font-pixel text-[8px] text-[color:var(--color-text-dim)] mb-0.5">
            超额收益 Alpha
          </div>
          <div className="font-pixel text-lg text-[color:var(--color-gold)]">
            +{alpha}%
          </div>
        </div>
        <div className="pixel-box p-2 text-center bg-[color:var(--color-bg-elevated)]">
          <div className="font-pixel text-[8px] text-[color:var(--color-text-dim)] mb-0.5">
            当前胜率
          </div>
          <div className="font-pixel text-lg text-[color:var(--color-cyan)]">
            {latest?.winRate}%
          </div>
        </div>
      </div>

      {/* Recharts 折线图容器 */}
      <div className="w-full h-56 relative font-pixel-body pt-1">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={performanceData}
            margin={{ top: 8, right: 12, left: -16, bottom: 0 }}
          >
            <CartesianGrid
              strokeDasharray="3 3"
              stroke="var(--color-border)"
              vertical={false}
            />
            <XAxis
              dataKey="trade"
              tick={{ fontSize: 10, fill: 'var(--color-text-dim)' }}
              stroke="var(--color-border-bright)"
            />
            <YAxis
              tick={{ fontSize: 10, fill: 'var(--color-text-dim)' }}
              stroke="var(--color-border-bright)"
              unit="%"
            />
            <Tooltip content={<PixelTooltip />} />
            <Legend
              wrapperStyle={{ fontSize: '11px', paddingTop: '8px' }}
              iconType="rect"
            />
            <ReferenceLine y={0} stroke="var(--color-text-dim)" strokeDasharray="3 3" />

            {(metricView === 'all' || metricView === 'pnl') && (
              <Line
                type="monotone"
                dataKey="pnl"
                name="模拟收益率 (%)"
                stroke="var(--color-bull)"
                strokeWidth={2.5}
                dot={{ r: 3.5, fill: 'var(--color-bull)', strokeWidth: 1 }}
                activeDot={{ r: 6 }}
              />
            )}

            {(metricView === 'all' || metricView === 'pnl') && (
              <Line
                type="monotone"
                dataKey="benchmark"
                name="大盘基准 (%)"
                stroke="var(--color-text-dim)"
                strokeDasharray="4 4"
                strokeWidth={1.5}
                dot={{ r: 2 }}
              />
            )}

            {(metricView === 'all' || metricView === 'winRate') && (
              <Line
                type="monotone"
                dataKey="winRate"
                name="阶段胜率 (%)"
                stroke="var(--color-gold)"
                strokeWidth={2}
                dot={{ r: 3, fill: 'var(--color-gold)' }}
              />
            )}
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* 底部备注 */}
      <div className="mt-2 text-right">
        <span className="font-pixel-display text-[9px] text-[color:var(--color-text-dim)]">
          * 根据实操答题纪律与胜率计算的模拟收益表现
        </span>
      </div>
    </div>
  )
}
